import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  type AdminOrg,
  useAdminDeleteOrg,
  useAdminUpdateOrg,
} from '@/lib/admin';

interface Props {
  org: AdminOrg;
  onClose: () => void;
}

export function EditOrgModal({ org, onClose }: Props) {
  const update = useAdminUpdateOrg();
  const del = useAdminDeleteOrg();

  const [name, setName] = useState(org.name);
  const [slug, setSlug] = useState(org.slug);
  const [prefix, setPrefix] = useState(org.part_number_prefix);
  const [preferDetailed, setPreferDetailed] = useState(
    org.prefer_detailed_description,
  );

  const onSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await update.mutateAsync({
        orgSlug: org.slug,
        input: {
          name: name !== org.name ? name.trim() : undefined,
          slug: slug !== org.slug ? slug.trim() : undefined,
          part_number_prefix:
            prefix !== org.part_number_prefix ? prefix.trim() : undefined,
          prefer_detailed_description:
            preferDetailed !== org.prefer_detailed_description
              ? preferDetailed
              : undefined,
        },
      });
      onClose();
    } catch {
      /* surfaced below */
    }
  };

  const onDelete = async () => {
    if (
      !confirm(
        `Force-delete "${org.name}"? Removes every part, BOM, supplier link, location, image, and pending invitation in this org. Members may be left orphaned (no orgs). This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await del.mutateAsync(org.slug);
      onClose();
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h2 className="text-lg font-semibold">
            Edit <span className="font-mono">{org.slug}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <form onSubmit={onSave} className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                  Name
                </span>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                  Slug (URL piece)
                </span>
                <Input
                  value={slug}
                  onChange={(e) =>
                    setSlug(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]+/g, '-')
                        .replace(/^-+|-+$/g, ''),
                    )
                  }
                  className="font-mono"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                  Part-number prefix
                </span>
                <Input
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                  maxLength={16}
                  className="w-32 font-mono"
                />
              </label>
            </div>

            <label className="flex items-start gap-3 rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950 p-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={preferDetailed}
                onChange={(e) => setPreferDetailed(e.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium">
                  Prefer detailed vendor descriptions
                </span>
                <span className="mt-0.5 block text-stone-600 dark:text-stone-400">
                  Use DigiKey's long-form description on validate /
                  bulk-refresh / create-from-URL.
                </span>
              </span>
            </label>

            {update.isError && (
              <p className="text-sm text-red-600">
                {(update.error as Error).message}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>

          <section className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50/30 dark:bg-red-950/20 p-4">
            <h3 className="font-semibold text-red-700 dark:text-red-400">
              Force delete
            </h3>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
              Removes every part, BOM, supplier link, location, image, and
              pending invitation. Cascades through every member's
              membership row — those users may end up with no orgs at
              all. Use sparingly.
            </p>
            <Button
              variant="danger"
              className="mt-3"
              onClick={onDelete}
              disabled={del.isPending}
            >
              {del.isPending ? 'Deleting…' : 'Delete organization'}
            </Button>
            {del.isError && (
              <p className="mt-2 text-sm text-red-600">
                {(del.error as Error).message}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
